import { QueryError } from '@graphql-box/helpers';

import { useQuery } from '@graphql-box/react';
import { ScreenType } from '@tmdb-graphql-api/schema/types';
import { type ReactElement, useEffect } from 'react';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { Component } from './Component.tsx';
import { formatWatchProviders } from './helpers/formatWatchProviders.ts';
import { type MovieWatchProvidersData, type TvWatchProvidersData } from './types.ts';

const queries = {
  [ScreenType.Movie]: "\nquery GetMovieWatchProviders(\n  $id: ID!\n) {\n  movie(id: $id) {\n    ...MovieWatchProviders\n  }\n}\n\nfragment MovieWatchProviders on Movie {\n  watchProviders {\n    GB {\n      ads {\n        displayPriority\n        logoPath\n        providerName\n      }\n      buy {\n        displayPriority\n        logoPath\n        providerName\n      }\n      flatrate {\n        displayPriority\n        logoPath\n        providerName\n      }\n      free {\n        displayPriority\n        logoPath\n        providerName\n      }\n      rent {\n        displayPriority\n        logoPath\n        providerName\n      }\n    }\n  }\n}\n",
  [ScreenType.Tv]: "\nquery GetTvWatchProviders(\n  $id: ID!\n) {\n  tv(id: $id) {\n    ...TvWatchProviders\n  }\n}\n\nfragment TvWatchProviders on Tv {\n  watchProviders {\n    GB {\n      ads {\n        displayPriority\n        logoPath\n        providerName\n      }\n      buy {\n        displayPriority\n        logoPath\n        providerName\n      }\n      flatrate {\n        displayPriority\n        logoPath\n        providerName\n      }\n      free {\n        displayPriority\n        logoPath\n        providerName\n      }\n      rent {\n        displayPriority\n        logoPath\n        providerName\n      }\n    }\n  }\n}\n",
};

export type WatchProvidersQueryProps = {
  children?: (element?: ReactElement) => ReactElement | undefined;
  id: string | number;
  type: ScreenType;
};

export const WatchProvidersQuery = ({ children, id, type }: WatchProvidersQueryProps) => {
  const { config } = useToolbelt();
  const query = queries[type];

  const { execute, loading, result } = useQuery<MovieWatchProvidersData | TvWatchProvidersData>(query, {
    loading: true,
  });

  useEffect(() => {
    void execute({ variables: { id } }, { data: { initiator: 'WatchProviders' } });
    // We only want to re-execute when id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (result instanceof QueryError) {
    console.error(result);
    return;
  }

  if (result?.errors?.length) {
    console.error(result.errors);
  }

  const providers = formatWatchProviders(result?.data, config);

  if (!loading && providers.length === 0) {
    return;
  }

  return children ? children(<Component providers={providers} />) : <Component providers={providers} />;
};
